import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Package, Check, CreditCard, Loader2, ShieldCheck, Layers, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogBody } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Actions as PAYMENTACTIONS } from '../store/actions/paymentActions';
import Breadcrumb from '../components/Common/Breadcrumb';
import { formatFeatureKey } from '../components/utils/smePlans';
import { getEntitlementFeatureKeys } from '../components/utils/subscriptionAccess';

const breadcrumbItems = [{ label: 'Add-On Catalog', link: '/addons' }];

const formatPrice = (minorUnits, currency = 'gbp') => {
  if (minorUnits == null) return null;
  const major = minorUnits / 100;
  const symbols = { gbp: '£', usd: '$', eur: '€' };
  const sym = symbols[currency] || currency.toUpperCase() + ' ';
  return `${sym}${major.toFixed(2)}`;
};

const BundleBadge = ({ addonIds, orgAddOns }) => {
  if (!addonIds || addonIds.length <= 1) return null;
  const ownedCount = addonIds.filter((id) => (orgAddOns || []).includes(id)).length;
  if (ownedCount === 0) return (
    <Badge className='bg-primary/10 text-primary text-[10px]'>
      <Layers size={10} strokeWidth={1.5} className='mr-0.5' />
      Bundle ({addonIds.length} datasets)
    </Badge>
  );
  return (
    <Badge className='bg-info-bg text-info text-[10px]'>
      <Layers size={10} strokeWidth={1.5} className='mr-0.5' />
      {ownedCount} of {addonIds.length} datasets owned
    </Badge>
  );
};

/** Percentage saved by paying annually, or `null` when there is no saving to advertise. */
const annualSavingPercent = (annual, monthly) => {
  if (annual == null || !monthly) return null;
  const percent = Math.round((1 - annual / (monthly * 12)) * 100);
  return percent > 0 ? percent : null;
};

const BillingTypeOption = ({ label, price, suffix, note, selected, onSelect }) => (
  <Button
    type='button'
    variant='outline'
    aria-pressed={selected}
    onClick={onSelect}
    className={`h-auto w-full flex-col items-start justify-start gap-1.5 whitespace-normal px-3.5 py-3 text-left ${
      selected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:border-primary/40'
    }`}>
    <span className='flex w-full items-center justify-between gap-2'>
      <span className='text-sm font-semibold text-foreground'>{label}</span>
      {selected && <Check size={14} strokeWidth={2} className='text-primary' />}
    </span>
    <span className='text-base font-semibold text-foreground'>
      {price}
      <span className='text-xs font-normal text-muted-foreground'>{suffix}</span>
    </span>
    {note}
  </Button>
);

const ProductUnlocks = ({ featureKeys }) => {
  if (!featureKeys?.length) return null;
  return (
    <div className='mb-3'>
      <p className='mb-1 text-xs font-medium text-muted-foreground'>Unlocks</p>
      <div className='flex flex-wrap gap-1'>
        {featureKeys.map((key) => (
          <Badge key={key} variant='outline' className='text-[10px]'>
            {formatFeatureKey(key)}
          </Badge>
        ))}
      </div>
    </div>
  );
};

const AddOnCatalog = ({
  catalog,
  catalogLoading,
  purchaseLoading,
  orgAddOns,
  getAddonCatalog,
  purchaseAddOn,
  cancelAddOn,
  stripeCustomerId,
  orgDetails,
}) => {
  const [purchaseDialog, setPurchaseDialog] = useState(null);
  const [billingType, setBillingType] = useState('annual');
  const [cancelDialog, setCancelDialog] = useState(null);

  useEffect(() => {
    getAddonCatalog();
  }, [getAddonCatalog]);

  const handlePurchase = useCallback(async () => {
    if (!purchaseDialog) return;
    const result = await purchaseAddOn(purchaseDialog.id, billingType);
    if (result?.success) {
      setPurchaseDialog(null);
      setBillingType('annual');
    }
  }, [purchaseDialog, billingType, purchaseAddOn]);

  const handleCancel = useCallback(async () => {
    if (!cancelDialog) return;
    const result = await cancelAddOn(cancelDialog.id);
    if (result?.success) setCancelDialog(null);
  }, [cancelDialog, cancelAddOn]);

  const resolvedFeatureKeys = getEntitlementFeatureKeys(orgDetails);
  const isIncludedByPlan = useCallback(
    (product) =>
      !product.entitled &&
      !(product.addon_ids || []).length &&
      (product.feature_keys || []).length > 0 &&
      product.feature_keys.every((key) => resolvedFeatureKeys.includes(key)),
    [resolvedFeatureKeys],
  );
  const entitled = useMemo(
    () => (catalog || []).filter((product) => product.entitled || isIncludedByPlan(product)),
    [catalog, isIncludedByPlan],
  );
  const available = useMemo(
    () => (catalog || []).filter((product) => !product.entitled && !isIncludedByPlan(product)),
    [catalog, isIncludedByPlan],
  );
  const hasActivePaidSubscription =
    orgDetails?.hasSubscriptionActive === true &&
    orgDetails?.subscriptionStatus !== 'trialing' &&
    !!stripeCustomerId;

  return (
    <TooltipProvider>
      <div className='flex flex-col gap-6 p-6'>
        <div className='flex justify-between items-center'>
          <div>
            <Breadcrumb items={breadcrumbItems} />
            <div className='flex items-center gap-2 mt-2'>
              <Package size={24} strokeWidth={1.5} className='text-primary' />
              <h1 className='text-2xl font-bold text-foreground'>Add-Ons</h1>
            </div>
            <p className='text-sm text-muted-foreground mt-1'>
              Add optional datasets and software features without changing your core plan.
            </p>
          </div>
          <Button variant='outline' size='sm' onClick={() => getAddonCatalog()} loading={catalogLoading}>
            Refresh
          </Button>
        </div>

        {!hasActivePaidSubscription && (
          <Alert variant='info'>
            <AlertDescription className='flex flex-wrap items-center justify-between gap-3'>
              <span>Add-ons can only be purchased with an active paid subscription.</span>
              <Button asChild size='sm'>
                <Link to='/plans'>Choose a plan</Link>
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {catalogLoading && !catalog?.length && (
          <div className='flex items-center justify-center py-16'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        )}

        {entitled.length > 0 && (
          <div>
            <h2 className='text-lg font-semibold text-foreground mb-3 flex items-center gap-2'>
              <ShieldCheck size={18} strokeWidth={1.5} className='text-success' />
              Your Active Products
            </h2>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
              {entitled.map((product) => {
                const isPendingCancel = product.cancellation_date || product.pending_cancellation;
                const includedByPlan = isIncludedByPlan(product);
                return (
                  <Card key={product.id} className={isPendingCancel ? 'border-warning/30 bg-warning-bg/5' : 'border-success/30 bg-success-bg/5'}>
                    <CardHeader className='pb-2'>
                      <div className='flex items-center justify-between gap-2'>
                        <CardTitle className='text-sm font-semibold text-foreground'>
                          {product.name}
                        </CardTitle>
                        <div className='flex items-center gap-1.5'>
                          <BundleBadge addonIds={product.addon_ids} orgAddOns={orgAddOns} />
                          {includedByPlan ? (
                            <Badge className='bg-info-bg text-info border-info/30'>Included with plan</Badge>
                          ) : isPendingCancel ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge className='bg-warning-bg text-warning border-warning/30'>
                                  <Clock size={10} strokeWidth={2} className='mr-1' />
                                  Cancelling
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                Access until {new Date(product.cancellation_date).toLocaleDateString()}
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <Badge className='bg-success-bg text-success border-success/30'>
                              <Check size={12} strokeWidth={2} className='mr-1' />
                              Active
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {product.description && (
                        <p className='text-xs text-muted-foreground mb-3'>{product.description}</p>
                      )}
                      <ProductUnlocks featureKeys={product.feature_keys} />
                      {!includedByPlan && !isPendingCancel && (
                        <Button
                          variant='outline'
                          size='sm'
                          className='text-xs text-danger border-danger/30 hover:bg-danger/5'
                          onClick={() => setCancelDialog(product)}
                        >
                          Cancel
                        </Button>
                      )}
                      {isPendingCancel && product.cancellation_date && (
                        <p className='text-xs text-warning'>
                          Access remains until {new Date(product.cancellation_date).toLocaleDateString()}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {available.length > 0 && (
          <div>
            <h2 className='text-lg font-semibold text-foreground mb-3 flex items-center gap-2'>
              <Package size={18} strokeWidth={1.5} className='text-primary' />
              Available Products
            </h2>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
              {available.map((product) => {
                const partialOwned = (product.addon_ids || []).filter((id) => (orgAddOns || []).includes(id)).length;
                const totalInBundle = (product.addon_ids || []).length;
                return (
                  <Card key={product.id} className='hover:border-primary/30 transition-colors'>
                    <CardHeader className='pb-2'>
                      <div className='flex items-center justify-between gap-2'>
                        <CardTitle className='text-sm font-semibold text-foreground'>
                          {product.name}
                        </CardTitle>
                        <BundleBadge addonIds={product.addon_ids} orgAddOns={orgAddOns} />
                      </div>
                    </CardHeader>
                    <CardContent>
                      {product.description && (
                        <p className='text-xs text-muted-foreground mb-2'>{product.description}</p>
                      )}
                      <ProductUnlocks featureKeys={product.feature_keys} />
                      {partialOwned > 0 && totalInBundle > 1 && (
                        <p className='text-[10px] text-info mb-2'>
                          You already have {partialOwned} of {totalInBundle} datasets in this bundle.
                        </p>
                      )}
                      <div className='space-y-2'>
                        {product.price_display && (
                          <p className='text-sm font-medium text-foreground'>{product.price_display}</p>
                        )}
                        {!product.price_display && (product.price_monthly || product.price_annual) && (
                          <div className='text-sm font-medium text-foreground space-y-0.5'>
                            {product.price_monthly != null && (
                              <p>{formatPrice(product.price_monthly, product.currency)}/mo</p>
                            )}
                            {product.price_annual != null && (
                              <p>{formatPrice(product.price_annual, product.currency)}/yr</p>
                            )}
                          </div>
                        )}
                        {hasActivePaidSubscription && (product.price_annual != null || product.price_monthly != null) ? (
                          <Button
                            size='sm'
                            className='w-full'
                            onClick={() => {
                              setPurchaseDialog(product);
                              setBillingType(product.price_annual != null ? 'annual' : 'monthly');
                            }}>
                            <CreditCard size={14} strokeWidth={1.5} className='mr-1' />
                            Purchase
                          </Button>
                        ) : !hasActivePaidSubscription ? (
                          <Button asChild size='sm' variant='outline' className='w-full'>
                            <Link to='/plans'>Choose a plan</Link>
                          </Button>
                        ) : (
                          <p className='text-xs text-muted-foreground'>No purchasable billing interval is configured.</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {!catalogLoading && !catalog?.length && (
          <div className='text-center py-16'>
            <Package size={40} strokeWidth={1} className='mx-auto text-muted-foreground mb-3' />
            <p className='text-muted-foreground'>No add-on products are currently available.</p>
          </div>
        )}

        <Dialog open={!!purchaseDialog} onOpenChange={(open) => !open && setPurchaseDialog(null)}>
          <DialogContent className='max-w-md'>
            <DialogHeader>
              <DialogTitle>Purchase Add-On</DialogTitle>
            </DialogHeader>
            <DialogBody>
              {purchaseDialog && (
                <div className='space-y-5'>
                  <div className='space-y-2'>
                    <p className='text-base font-semibold text-foreground'>{purchaseDialog.name}</p>
                    {purchaseDialog.description && (
                      <p className='text-sm text-muted-foreground'>{purchaseDialog.description}</p>
                    )}
                    {(purchaseDialog.addon_ids || []).length > 1 && (
                      <Badge className='bg-info-bg text-info text-[10px]'>
                        <Layers size={10} strokeWidth={1.5} className='mr-0.5' />
                        Bundle of {purchaseDialog.addon_ids.length} datasets
                      </Badge>
                    )}
                    <ProductUnlocks featureKeys={purchaseDialog.feature_keys} />
                  </div>

                  <div className='space-y-2'>
                    <p className='text-xs font-medium text-muted-foreground uppercase tracking-wider'>Billing type</p>
                    <div className='grid gap-2 sm:grid-cols-2'>
                      {purchaseDialog.price_annual != null && (
                        <BillingTypeOption
                          label='Annual'
                          price={formatPrice(purchaseDialog.price_annual, purchaseDialog.currency)}
                          suffix='/yr'
                          selected={billingType === 'annual'}
                          onSelect={() => setBillingType('annual')}
                          note={
                            annualSavingPercent(purchaseDialog.price_annual, purchaseDialog.price_monthly) && (
                              <Badge className='bg-success-bg text-success text-[10px]'>
                                Save {annualSavingPercent(purchaseDialog.price_annual, purchaseDialog.price_monthly)}%
                              </Badge>
                            )
                          }
                        />
                      )}
                      {purchaseDialog.price_monthly != null && (
                        <BillingTypeOption
                          label='Monthly'
                          price={formatPrice(purchaseDialog.price_monthly, purchaseDialog.currency)}
                          suffix='/mo'
                          selected={billingType === 'monthly'}
                          onSelect={() => setBillingType('monthly')}
                        />
                      )}
                    </div>
                    {purchaseDialog.min_term_months ? (
                      <p className='flex items-center gap-1.5 text-xs text-muted-foreground'>
                        <Clock size={12} strokeWidth={1.5} />
                        Minimum term of {purchaseDialog.min_term_months} months applies.
                      </p>
                    ) : null}
                  </div>

                  <Alert>
                    <AlertDescription className='text-xs'>
                      {billingType === 'annual'
                        ? 'The subscription item renews aligned with your main subscription. Stripe charges in your subscription\'s currency automatically.'
                        : 'Monthly billing starts now with proration to your next billing cycle. A minimum commitment period applies.'}
                    </AlertDescription>
                  </Alert>
                </div>
              )}
            </DialogBody>
            <DialogFooter>
              <Button variant='outline' onClick={() => setPurchaseDialog(null)}>Cancel</Button>
              <Button onClick={handlePurchase} loading={purchaseLoading}>
                <CreditCard size={14} strokeWidth={1.5} className='mr-1' />
                Confirm Purchase
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!cancelDialog} onOpenChange={(open) => !open && setCancelDialog(null)}>
          <DialogContent className='max-w-sm'>
            <DialogHeader>
              <DialogTitle>Cancel Add-On</DialogTitle>
            </DialogHeader>
            <DialogBody>
              {cancelDialog && (
                <div className='space-y-3'>
                  <p className='text-sm text-foreground'>
                    Are you sure you want to cancel <strong>{cancelDialog.name}</strong>?
                  </p>
                  {(cancelDialog.addon_ids || []).length > 1 && (
                    <Alert variant='info'>
                      <AlertDescription className='text-xs'>
                        This bundle includes {cancelDialog.addon_ids.length} datasets. All will be removed
                        at the end of the billing period unless also covered by another purchase.
                      </AlertDescription>
                    </Alert>
                  )}
                  {(cancelDialog.feature_keys || []).length > 0 && (
                    <Alert variant='info'>
                      <AlertDescription className='text-xs'>
                        Access to {cancelDialog.feature_keys.map(formatFeatureKey).join(', ')} remains until the cancellation
                        date, then the feature will be removed unless included by your plan or another add-on.
                      </AlertDescription>
                    </Alert>
                  )}
                  <Alert variant='warning'>
                    <AlertDescription className='text-xs'>
                      If a minimum term applies, cancellation may be blocked until it's fulfilled.
                      Access continues until the end of your current billing period.
                    </AlertDescription>
                  </Alert>
                </div>
              )}
            </DialogBody>
            <DialogFooter>
              <Button variant='outline' onClick={() => setCancelDialog(null)}>Keep Add-On</Button>
              <Button variant='destructive' onClick={handleCancel}>
                Confirm Cancellation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

const mapStateToProps = (state) => ({
  catalog: state.payment?.addonCatalog || [],
  catalogLoading: state.payment?.addonCatalogLoading || false,
  purchaseLoading: state.payment?.addonPurchaseLoading || false,
  orgAddOns: state.organisation?.details?.addOns || [],
  orgDetails: state.organisation?.details,
  stripeCustomerId: state.payment?.stripe?.stripeCustomerId,
});

const mapDispatchToProps = (dispatch) => ({
  getAddonCatalog: () => dispatch(PAYMENTACTIONS.getAddonCatalog()),
  purchaseAddOn: (id, t) => dispatch(PAYMENTACTIONS.purchaseAddOn(id, t)),
  cancelAddOn: (id) => dispatch(PAYMENTACTIONS.cancelAddOn(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddOnCatalog);
